/**
 * EmailSnoozePopover.jsx — Wave 1u3 Phase D / F4 (snooze a thread)
 *
 * Lightweight popover that lets the user pick when an email should
 * "wake up" in their inbox. Four presets + custom date/time picker.
 *
 * Presets (computed at click time so they're always relative to "now"):
 *   - Cet après-midi  : today at 15:00 (skipped if it's already past 14:00 → next day 09:00)
 *   - Ce soir        : today at 19:00 (skipped if past 18:00)
 *   - Demain matin   : tomorrow at 09:00
 *   - Lundi prochain : next Monday at 09:00
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onSnooze: (isoUntil: string) => Promise<void>
 *   - currentSnoozedUntil?: string (ISO) — shown as "Reporté jusqu'au …" with an unsnooze button
 *   - onUnsnooze?: () => Promise<void>
 */
import React, { useState } from 'react';
import { Clock, Calendar, X, Coffee, Sunrise, Sunset, AlarmClockOff, Loader2 } from 'lucide-react';

function nextWeekdayAt9(targetDay /* 1=Mon … 7=Sun */) {
  const d = new Date();
  const cur = d.getDay() === 0 ? 7 : d.getDay(); // ISO 1..7
  let delta = (targetDay - cur + 7) % 7;
  if (delta === 0) delta = 7; // always "next" Monday, not today
  d.setDate(d.getDate() + delta);
  d.setHours(9, 0, 0, 0);
  return d;
}

function todayAt(hour) {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  return d;
}

function tomorrowAt(hour) {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function fmtFR(d) {
  return d.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function EmailSnoozePopover({
  open,
  onClose,
  onSnooze,
  currentSnoozedUntil,
  onUnsnooze,
}) {
  const now = new Date();
  const [busy, setBusy] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('09:00');

  const presets = [];
  const afternoon = todayAt(15);
  if (now.getHours() < 14) presets.push({ id: 'afternoon', label: 'Cet après-midi', desc: fmtFR(afternoon), icon: Coffee, dt: afternoon });
  const evening = todayAt(19);
  if (now.getHours() < 18) presets.push({ id: 'evening', label: 'Ce soir', desc: fmtFR(evening), icon: Sunset, dt: evening });
  presets.push({ id: 'tomorrow', label: 'Demain matin', desc: fmtFR(tomorrowAt(9)), icon: Sunrise, dt: tomorrowAt(9) });
  presets.push({ id: 'monday', label: 'Lundi prochain', desc: fmtFR(nextWeekdayAt9(1)), icon: Calendar, dt: nextWeekdayAt9(1) });

  const handleSnooze = async (dt) => {
    if (busy) return;
    setBusy(true);
    try {
      await onSnooze?.(dt.toISOString());
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleCustom = async () => {
    if (!customDate || !customTime) return;
    const dt = new Date(`${customDate}T${customTime}`);
    if (isNaN(dt.getTime()) || dt <= new Date()) return;
    await handleSnooze(dt);
  };

  const handleUnsnooze = async () => {
    if (busy) return;
    setBusy(true);
    try { await onUnsnooze?.(); onClose?.(); } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="email-snooze-popover">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-[#1A1B2E] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#4F46E5]" /> Reporter ce message
          </h3>
          <button onClick={onClose} className="text-[#6B6C85] hover:text-[#1A1B2E]" data-testid="email-snooze-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        {currentSnoozedUntil && (
          <div className="px-4 py-2.5 bg-[#FFF7ED] border-b border-[#FED7AA] flex items-center justify-between gap-2" data-testid="email-snooze-active">
            <div className="text-[11.5px] text-[#9A3412]">
              <strong>Reporté</strong> jusqu'au{' '}
              <span className="font-mono text-[11px]">{fmtFR(new Date(currentSnoozedUntil))}</span>
            </div>
            <button
              type="button"
              onClick={handleUnsnooze}
              disabled={busy}
              data-testid="email-snooze-unsnooze"
              className="h-7 px-2 rounded text-[11px] font-semibold border border-[#FCA5A5] bg-white text-[#B91C1C] hover:bg-[#FEF2F2] inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <AlarmClockOff className="w-3 h-3" />}
              Annuler
            </button>
          </div>
        )}

        <div className="p-2">
          {presets.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handleSnooze(p.dt)}
                disabled={busy}
                data-testid={`email-snooze-preset-${p.id}`}
                className="w-full text-left flex items-center gap-3 px-3 py-2 rounded hover:bg-[#FAFAFE] disabled:opacity-50 group"
              >
                <Icon className="w-4 h-4 text-[#6B6C85] group-hover:text-[#4F46E5]" />
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E]">{p.label}</div>
                  <div className="text-[11px] text-[#8E8FA5]">{p.desc}</div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-3 pb-3 pt-1 border-t border-[#EFF0F6]" data-testid="email-snooze-custom">
          <div className="text-[11px] font-semibold text-[#6B6C85] mb-1.5">Date personnalisée</div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="email-snooze-date"
              className="flex-1 h-8 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
            />
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              data-testid="email-snooze-time"
              className="w-24 h-8 px-2 text-[12px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
            />
            <button
              type="button"
              onClick={handleCustom}
              disabled={busy || !customDate}
              data-testid="email-snooze-custom-confirm"
              className="h-8 px-3 rounded bg-[#4F46E5] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
